import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { excerpt, type ProjectDetailDto, type ProjectImageDto } from '@portfolio/shared';
import { ApiError } from '@/lib/api';
import { cn, paragraphs } from '@/lib/utils';
import { useProject, useSite } from '../hooks';
import { useNavTone } from '../navTone';
import { projectPeriod } from '../ProjectCard';
import { Reveal } from '../Reveal';
import { Seo } from '../Seo';
import { TestimonialFeature } from '../TestimonialFeature';
import { useLocale } from '../i18n';
import { NotFoundPage, PageError, PageLoader } from './states';

/** Body copy shared by the overview and the bilingual story: light, justified, airy. */
const bodyText = 'text-body text-graphite text-justify hyphens-auto';

function Paragraphs({ text, className }: { text: string | null | undefined; className?: string }) {
  return (
    <div className={cn(bodyText, 'space-y-[1em]', className)}>
      {paragraphs(text).map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  );
}

// ───────── cover + title card ─────────

function Cover({ project }: { project: ProjectDetailDto }) {
  const img = project.coverImage;
  return (
    <div className="relative h-[clamp(420px,59.6vw,860px)] overflow-hidden rounded-b-[32px] bg-gradient-to-b from-black to-smoke md:rounded-b-[60px]">
      {img && (
        <>
          <img
            src={img.url}
            srcSet={
              img.thumbUrl ? `${img.thumbUrl} 720w, ${img.url} ${img.width ?? 2400}w` : undefined
            }
            sizes="100vw"
            alt={img.alt ?? project.title}
            width={img.width ?? undefined}
            height={img.height ?? undefined}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 size-full object-cover"
          />
          {/* keeps the light navbar legible over bright covers */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent"
          />
        </>
      )}
    </div>
  );
}

function TitleCard({ project }: { project: ProjectDetailDto }) {
  const { data: site } = useSite();
  const category = project.category && (site?.categoryLabels[project.category] ?? project.category);
  const meta = [category, ...project.disciplines].filter(Boolean).join(' · ');
  return (
    <div className="container-site relative z-10 -mt-[96px] md:-mt-[143px]">
      <div className="w-full max-w-[645px] rounded-[22px] bg-card px-6 pt-7 pb-7 shadow-[0_34px_60px_-18px_rgb(0_0_0/0.22)] sm:px-10 md:rounded-[28px] md:px-[54px] md:pt-[38px] md:pb-[40px]">
        <h1 className="text-[clamp(2.75rem,1.2rem+6.3vw,6.25rem)] leading-[0.85] font-black tracking-[-0.015em] text-balance text-strong">
          {project.title}
        </h1>
        {meta && <p className="mt-4 text-lead font-light text-silver md:mt-[10px]">{meta}</p>}
      </div>
    </div>
  );
}

// ───────── overview + facts ─────────

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] items-baseline gap-x-4 border-b border-hairline py-5 first:pt-0 last:border-0 last:pb-0 sm:grid-cols-[214px_minmax(0,1fr)] sm:gap-x-0 md:pt-[26px] md:pb-[30px] md:first:pt-[5px] md:last:pb-[3px]">
      <dt className="text-title-sm font-light text-graphite">{label}</dt>
      <dd className="text-title-sm font-medium whitespace-pre-line text-ink">{children}</dd>
    </div>
  );
}

function Overview({ project }: { project: ProjectDetailDto }) {
  const { t } = useLocale();
  const scope = project.scope ?? (project.disciplines.join(t.listJoin) || null);
  const period = projectPeriod(project);
  return (
    <section
      aria-labelledby="overview-title"
      className="container-site mt-14 grid gap-12 md:mt-[77px] lg:grid-cols-[minmax(0,439fr)_minmax(0,593fr)] lg:gap-x-[96px]"
    >
      <div>
        <p className="text-lead font-light text-graphite">{t.project.overview}</p>
        <h2 id="overview-title" className="mt-1 text-h2 font-bold text-strong lg:-me-[60px]">
          {project.headline ?? t.project.about(project.title)}
        </h2>
        {project.description && <Paragraphs text={project.description} className="mt-5 md:mt-4" />}
      </div>

      <dl className="lg:border-s lg:border-graphite lg:ps-[56px]">
        {project.client && <Fact label={t.project.client}>{project.client}</Fact>}
        {scope && <Fact label={t.project.scope}>{scope}</Fact>}
        {period && <Fact label={t.project.year}>{period}</Fact>}
        {project.stage && (
          <Fact label={t.project.status}>
            <span
              className={cn(
                'inline-flex h-8 items-center rounded-full px-3.5 font-semibold text-white sm:-ms-[18px] md:h-10 md:px-[18px]',
                project.stage === 'ACTIVE'
                  ? 'bg-gradient-to-r from-go-light to-go shadow-[0_8px_20px_-4px_rgb(70_177_66/0.55)]'
                  : 'bg-ink text-canvas',
              )}
            >
              {t.project.stage[project.stage]}
            </span>
          </Fact>
        )}
        {project.projectUrl && (
          <Fact label={t.project.link}>
            <a
              href={project.projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 underline decoration-hairline decoration-2 underline-offset-[6px] transition-colors hover:text-primary hover:decoration-primary"
            >
              {t.project.visit}{' '}
              <ArrowUpRight className="size-[0.8em] rtl:-scale-x-100" aria-hidden />
            </a>
          </Fact>
        )}
      </dl>
    </section>
  );
}

// ───────── gallery + story ─────────

function Gallery({ images, title }: { images: ProjectImageDto[]; title: string }) {
  if (!images.length) return null;
  return (
    <div className="container-site space-y-5 md:space-y-8">
      {images.map((img) => (
        <Reveal key={img.id}>
          <figure>
            <div className="aspect-video overflow-hidden rounded-[20px] bg-gradient-to-b from-night to-smoke shadow-[0_24px_40px_-18px_rgb(0_0_0/0.45)] md:rounded-[28px]">
              <img
                src={img.media.url}
                srcSet={
                  img.media.thumbUrl
                    ? `${img.media.thumbUrl} 720w, ${img.media.url} ${img.media.width ?? 2400}w`
                    : undefined
                }
                sizes="(min-width: 1208px) 1128px, 100vw"
                alt={img.media.alt ?? img.caption ?? title}
                width={img.media.width ?? undefined}
                height={img.media.height ?? undefined}
                loading="lazy"
                decoding="async"
                className="size-full object-cover"
              />
            </div>
            {img.caption && (
              <figcaption className="mt-3 text-caption text-muted">{img.caption}</figcaption>
            )}
          </figure>
        </Reveal>
      ))}
    </div>
  );
}

/**
 * The bilingual story: English and Arabic side by side. The page's own language takes the first
 * column (left on English pages, right on Arabic ones); the other language sits beside it.
 */
function Story({ project }: { project: ProjectDetailDto }) {
  const { locale, t } = useLocale();
  const heading = 'max-w-[11ch] text-h2 font-bold text-balance text-strong';
  const blocks = [
    {
      lang: 'en' as const,
      title: project.storyTitle,
      body: project.storyBody,
    },
    {
      lang: 'ar' as const,
      title: project.storyTitleAr,
      body: project.storyBodyAr,
    },
  ]
    .filter((b) => b.title || b.body)
    .sort((a) => (a.lang === locale ? -1 : 1));
  return (
    <section
      aria-label={
        blocks.find((b) => b.lang === locale)?.title ?? blocks[0]?.title ?? t.project.story
      }
      className="container-site grid gap-14 py-16 md:grid-cols-2 md:gap-x-[clamp(48px,14.5vw,186px)] md:pt-[130px] md:pb-[150px]"
    >
      {blocks.map((b) => (
        <Reveal
          key={b.lang}
          className={cn(blocks.length === 1 && b.lang !== locale && 'md:col-start-2')}
        >
          <div dir={b.lang === 'ar' ? 'rtl' : 'ltr'} lang={b.lang}>
            {b.title && (
              <h2 className={cn(heading, b.lang === 'ar' && 'tracking-normal')}>{b.title}</h2>
            )}
            <Paragraphs
              text={b.body}
              className={cn('mt-8 md:mt-[35px]', b.lang === 'ar' && 'tracking-normal')}
            />
          </div>
        </Reveal>
      ))}
    </section>
  );
}

// ───────── page ─────────

/** Presentational project page — used publicly and for the admin's draft preview. */
export function ProjectView({ project }: { project: ProjectDetailDto }) {
  const { t } = useLocale();
  const hasStory = !!(
    project.storyTitle ||
    project.storyBody ||
    project.storyTitleAr ||
    project.storyBodyAr
  );
  // The story sits between the first two gallery images and the rest, as in the design.
  const split = hasStory ? Math.min(2, project.images.length) : project.images.length;
  const before = project.images.slice(0, split);
  const after = project.images.slice(split);
  const testimonials = project.testimonials;

  return (
    <article>
      <Cover project={project} />
      <TitleCard project={project} />
      <Overview project={project} />

      {before.length > 0 && (
        <div className="mt-14 md:mt-[70px]">
          <Gallery images={before} title={project.title} />
        </div>
      )}
      {hasStory ? <Story project={project} /> : <div className="h-16 md:h-[100px]" />}
      {after.length > 0 && (
        <div className="pb-16 md:pb-[100px]">
          <Gallery images={after} title={project.title} />
        </div>
      )}

      {testimonials.length > 0 && (
        <section
          aria-label={t.project.testimonial}
          className="bg-night pt-10 pb-4 md:pt-[67px] md:pb-[66px]"
        >
          <div className="container-site space-y-8">
            {testimonials.map((t) => (
              <Reveal key={t.id}>
                <TestimonialFeature t={t} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

export function ProjectPage() {
  const { slug = '' } = useParams();
  const { data, isLoading, error, refetch } = useProject(slug);
  const { data: site } = useSite();
  const { locale, to } = useLocale();
  useNavTone(data ? 'dark' : 'light');
  if (isLoading) return <PageLoader />;
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  if (error || !data) return <PageError error={error} onRetry={() => refetch()} />;
  const p = data.project;
  return (
    <>
      <Seo
        title={`${p.title} — ${site?.settings.ownerName ?? ''}`}
        description={excerpt(p.description) || undefined}
        image={p.coverImage?.url}
        path={`/work/${p.slug}`}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          name: p.title,
          description: excerpt(p.description) || undefined,
          image: p.coverImage?.url,
          dateCreated: p.year ? String(p.year) : undefined,
          creator: site ? { '@type': 'Person', name: site.settings.ownerName } : undefined,
          url: `${window.location.origin}${to(`/work/${p.slug}`)}`,
          inLanguage: locale,
        }}
      />
      <ProjectView project={p} />
    </>
  );
}
